import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import moment from "moment-timezone";

import { AGRISMART, ALERTS, DEVICES, get, post, PARCELS } from "./Client";
import { sortDevices } from "./ProjectService";
import { getWeatherForecast, calculateDewPoint, calculateWetBulbTemperature, calculateVaporPressureDeficit, calculateEvapotranspiration } from "./WeatherService";
import { evaluateRiskCicloconio } from "./AdversityCalculator";


export function useGetDevices() {
	let project = useSelector(store => store.session.project);
	let params = project ? {pid:project.id} : null;
	const {status, data, error} = useQuery(['devices', params], () =>
		get(`${AGRISMART}/${DEVICES}`, {params}), {
			enabled: !!project,
			select: data => data.sort((a, b) => sortDevices(a, b)),
		}
	);
	return {status, devices: data || [], error};
}

export function useGetParcels() {
	let project = useSelector(store => store.session.project);
	let params = project ? {project:project.id} : null;
	const {status, data, error} = useQuery(['parcels', params], () =>
		get(`${AGRISMART}/${PARCELS}`, {params}),
		{enabled: !!project}
	);
	return {status, parcels: data || [], error};
}

export function useGetAlerts(open) {
	let project = useSelector(store => store.session.project);
	let params = project ? {project:project.id} : null
	if(open !== undefined) params.open = open
	const {status, data, error} = useQuery(['alerts', params], () =>
		get(`${AGRISMART}/${ALERTS}`, {params}),
		{enabled: !!project}
	);
	return {status, alerts: data || [], error};
}


export function getTodayData(device) {
	return get(`${AGRISMART}/${device}/today`).then(elaborateData)
}

export function getWeeklyData(device) {
	return get(`${AGRISMART}/${device}/week`).then(elaborateData)
}

export function getMonthlyData(device) {
	return get(`${AGRISMART}/${device}/month`).then(elaborateData)
}

export function getCustomData(device, start, end) {
	return get(`${AGRISMART}/${device}/custom`, {params:{start,end}}).then(elaborateData)
}

function elaborateData(data) {
	data.forEach(d => {
		// calcola i valori meteo
		if (d.externalTemperature != null && d.externalHumidity != null) {
			d.dewPoint = calculateDewPoint(d.externalTemperature, d.externalHumidity)
			d.wetBulbTemperature = calculateWetBulbTemperature(d.externalTemperature, d.externalHumidity)
			d.vaporPressureDeficit = calculateVaporPressureDeficit(d.externalTemperature, d.externalHumidity)
			d.deltaTemperature = Number((d.externalTemperature - d.wetBulbTemperature).toFixed(2))
		}
		if (d.externalTemperature2 != null && d.externalHumidity2 != null) {
			d.dewPoint2 = calculateDewPoint(d.externalTemperature2, d.externalHumidity2)
			d.wetBulbTemperature2 = calculateWetBulbTemperature(d.externalTemperature2, d.externalHumidity2)
			d.vaporPressureDeficit2 = calculateVaporPressureDeficit(d.externalTemperature2, d.externalHumidity2)
			d.deltaTemperature2 = Number((d.externalTemperature2 - d.wetBulbTemperature2).toFixed(2))
		}
		// fix: le centraline prodotte dopo 03/2022 inviano 'UNKNOWN' per la direzione ovest
		if (d.windDirection === 'UNKNOWN') d.windDirection = 'O'
	})
	return data
}

export function getTodayDataProject(projectId, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		projectId,
		interval: 'today',
		exactTime: !!exactTime,
	}})
}

export function getWeeklyDataProject(projectId, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		projectId,
		interval: 'week',
		exactTime: !!exactTime,
	}})
}

export function getMonthlyDataProject(projectId, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		projectId,
		interval: 'month',
		exactTime: !!exactTime,
	}})
}

export function getCustomDataProject(projectId, startDate, endDate, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		projectId,
		interval: 'custom',
		startDate: startDate.format("YYYY-MM-DD"),
		endDate: endDate.format("YYYY-MM-DD"),
		exactTime: !!exactTime,
	}})
}

export function getTodayDataMany(deviceList, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		deviceSerials: deviceList.filter(a => !!a),
		interval: 'today',
		exactTime: !!exactTime,
	}}).then(r => elaborateDataMany(r.data, deviceList))
}

export function getWeeklyDataMany(deviceList, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		deviceSerials: deviceList.filter(a => !!a),
		interval: 'week',
		exactTime: !!exactTime,
	}}).then(r => elaborateDataMany(r.data, deviceList))
}

export function getMonthlyDataMany(deviceList, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		deviceSerials: deviceList.filter(a => !!a),
		interval: 'month',
		exactTime: !!exactTime,
	}}).then(r => elaborateDataMany(r.data, deviceList))
}

export function getCustomDataMany(deviceList, startDate, endDate, exactTime) {
	return post(`${AGRISMART}/deviceData`, { body: {
		deviceSerials: deviceList.filter(a => !!a),
		interval: 'custom',
		startDate: startDate.format("YYYY-MM-DD"),
		endDate: endDate.format("YYYY-MM-DD"),
		exactTime: !!exactTime,
	}}).then(r => elaborateDataMany(r.data, deviceList))
}

function elaborateDataMany(data, deviceList) {
	let cmpSize = deviceList.length
	// ottiene le date
	let dates = Array.from(new Set(data.map(d => d.receiptDate))).sort()

	// assegna indice della comparazione 0=RIF 1=A 2=B
	data.forEach(d => {
		d.cmpIdx = deviceList.findIndex(a => a === d.deviceId)
	})
	// per ogni data calcolo le differenze
	// assegno cmpIdx diverso per ogni combinazione 3=RIF-A 4=RIF-B 5=A-B
	dates.forEach(date => {
		let rif, a, b
		rif = data.find(d => d.deviceId && d.deviceId === deviceList[0] && d.receiptDate === date)
		a = data.find(d => d.deviceId && d.deviceId === deviceList[1] && d.receiptDate === date)
		if (cmpSize > 2) {
			b = data.find(d => d.deviceId && d.deviceId === deviceList[2] && d.receiptDate === date)
		}
		if (rif && a) {
			data.push({
				cmpIdx: cmpSize,
				receiptDate: date,
				diff_externalTemperature: (a.externalTemperature - rif.externalTemperature),
				diff_externalHumidity: (a.externalHumidity - rif.externalHumidity),
				diff_soilTemperature: (a.soilTemperature - rif.soilTemperature),
				diff_soilHumidity: (a.soilHumidity - rif.soilHumidity),
				diff_leafHumidity: (a.leafHumidity - rif.leafHumidity),
				diff_pluviometer: (a.pluviometer - rif.pluviometer),
				diff_radiation: (a.radiation - rif.radiation),
				diff_windSpeed: (a.windSpeed - rif.windSpeed),
				diff_atmPress: (a.atmPress - rif.atmPress),
			})
		}
		if (rif && b) {
			data.push({
				cmpIdx: cmpSize + 1,
				receiptDate: date,
				diff_externalTemperature: (b.externalTemperature - rif.externalTemperature),
				diff_externalHumidity: (b.externalHumidity - rif.externalHumidity),
				diff_soilTemperature: (b.soilTemperature - rif.soilTemperature),
				diff_soilHumidity: (b.soilHumidity - rif.soilHumidity),
				diff_leafHumidity: (b.leafHumidity - rif.leafHumidity),
				diff_pluviometer: (b.pluviometer - rif.pluviometer),
				diff_radiation: (b.radiation - rif.radiation),
				diff_windSpeed: (b.windSpeed - rif.windSpeed),
				diff_atmPress: (b.atmPress - rif.atmPress),
			})
		}
		if (a && b) {
			data.push({
				cmpIdx: cmpSize + 2,
				receiptDate: date,
				diff_externalTemperature: (a.externalTemperature - b.externalTemperature),
				diff_externalHumidity: (a.externalHumidity - b.externalHumidity),
				diff_soilTemperature: (a.soilTemperature - b.soilTemperature),
				diff_soilHumidity: (a.soilHumidity - b.soilHumidity),
				diff_leafHumidity: (a.leafHumidity - b.leafHumidity),
				diff_pluviometer: (a.pluviometer - b.pluviometer),
				diff_radiation: (a.radiation - b.radiation),
				diff_windSpeed: (a.windSpeed - b.windSpeed),
				diff_atmPress: (a.atmPress - b.atmPress),
			})
		}
	})
	return data
}


export async function getForecastData(device, cnt) {
	return getWeatherForecast(device.lat, device.lng, cnt)
		.then(result => result.data.list.map(d => {
			let dewPoint, wetBulbTemperature
			if (d?.main?.temp != null && d?.main?.humidity != null) {
				dewPoint = calculateDewPoint(d.main.temp, d.main.humidity)
				wetBulbTemperature = calculateWetBulbTemperature(d.main.temp, d.main.humidity)
			}
			return ({
				receiptDate: d.dt * 1000,
				externalTemperature: d?.main?.temp,
				externalHumidity: d?.main?.humidity,
				atmPress: d?.main?.pressure,
				windSpeed: d?.wind?.speed,
				pluviometer: d?.rain && d.rain['3h'],
				dewPoint,
				wetBulbTemperature,
			})
		}))
}

export function getForecastData1Day(device) {
	return getForecastData(device, 8)
}

export function getForecastData5Days(device) {
	return getForecastData(device)
}


export function getStatisticalData(device, start, end, type) {
	return get(`agrismart/${device}/statisticsDetails`, { params: { start, end } })
		.then(data => data.map(d => {
			return {
				atmPress: d.mediumExternalPressure,
				externalHumidity: d.mediumExternalHumidity,
				externalHumidity2: d.mediumExternalHumidity2,
				externalTemperature: d.mediumExternalTemperature,
				externalTemperature2: d.mediumExternalTemperature2,
				leafHumidity: d.mediumLeafHumidity,
				pluviometer: d.totalRainfall,		// precipitazione giornaliera
				radiation: d.mediumSolarRadiation,
				receiptDate: d.dataCollectionDate,
				soilHumidity: d.mediumSoilHumidity,
				soilHumidity2: d.mediumSoilHumidity2,
				soilTemperature: d.mediumSoilTemperature,
				soilTemperature2: d.mediumSoilTemperature2,
				windSpeed: d.mediumWindSpeed,
				wetHours: d.wetHours,
				ticchiolaturaDegreeHour: d.ticchiolaturaHoursTemperature,
			}
		}))
		.then(data => elaborateStatistics(data, type))
}

function elaborateStatistics(data, type) {
	data.forEach(d => {
		// calcolo il livello di rischio associato all'avversità
		let r
		if (type === 'olivo-cicloconio') {
			if (d.externalTemperature != null  && d.externalHumidity != null)
				r = evaluateRiskCicloconio(d.externalTemperature, d.externalHumidity, d.wetHours)
		}
		d.risk = r
		// calcola la evapotraspirazione giornaliera
		if (d.externalTemperature != null  && d.externalHumidity != null && d.radiation != null && d.windSpeed != null) {
			// stimo la radiazione giornaliera totale in Wh/m2 integrando la radiazione media su 24 ore
			let totalRadiation = d.radiation * 24.0
			d.evapotraspirazioneArboree  = calculateEvapotranspiration(d.externalTemperature, d.externalHumidity, totalRadiation, d.windSpeed, 2.0)
			d.evapotraspirazioneErbacee  = calculateEvapotranspiration(d.externalTemperature, d.externalHumidity, totalRadiation, d.windSpeed, 0.3)
		}
	})
	return data
}

/**
 * Prepara i dati agrismart per l'esportazione in formato csv
 * @param {*} data dati agrismart
 * @returns 
 */
export function toCSVdata(data) {
	return data.map(d => {
		return {
			Orario: moment(d.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm"),
			"Id Centralina": d.deviceId,
			"Temp esterna": d.externalTemperature,
			"Temp suolo 1": d.soilTemperature,
			"Temp suolo 2": d.soilTemperature2,
			"Hum atmosf": d.externalHumidity,
			"Hum suolo 1": d.soilHumidity,
			"Hum suolo 2": d.soilHumidity2,
			"Bagnatura fogliare": d.leafHumidity,
			"Piovosita": d.pluviometer,
			"Radiazione solare": d.radiation,
			"Temp esterna": d.externalTemperature2,
			"Hum atmosf 2": d.externalHumidity2,
			"Vel Vento": d.windSpeed,
			"Dir Vento": d.windDirection,
			"Pressione": d.atmPress,
		}
	})
}
